// R71 — THE MANDATORY "CREATE YOUR ROOM" MODAL OPENED OVER A MEMBER WHO ALREADY HAD ONE.
//
// **THE SIXTH ROUTE FOUND ANSWERING ITS OWN FAILURE AS A REAL ANSWER** (R55's
// `handleVideoProgress`, R60's `handleRoomBackgrounds` and `handleLalaMessages`, R64's
// `handleCauldronList`, R70's `handleSpellsBrowse`) — **and the first one where the forged fact is
// not a COLLECTION but an IDENTITY.** `handleProfile` caught its own database error and answered
//
//     200 { profile: null }
//
// which is byte-for-byte the sentence it sends a brand-new member who has not built her room yet.
// **THAT IS WHY THE TWO CENSUSES WALKED PAST IT** (R70's lesson: both listed by LITERAL, `|| []`
// and `r.ok ? … : { x: [] }`). Nothing here is fabricated with an array literal; a route simply
// says *"she has no room"* when the truth is *"we could not ask"*.
//
// **WHAT IT COSTS IS THE WORST PRICE THIS SEAM HAS CARRIED, BECAUSE THE FORGED ANSWER RAISES A
// DOOR SHE CANNOT SHUT.** App.tsx mounts the onboarding step on
//
//     isLoggedIn && profileChecked && !roomProfile && roomBuildStatus === "idle" && !membershipOpen
//
// and the block's own comment says **"Not dismissible until the room exists."** So ONE failed read
// locks a paying member out of her own house behind a form asking her to build a second one — and
// that form POSTs `/api/room/create`, which fires the signup generations. **A forged read that ends
// in a PAID call, on a member who already paid for the same three readings.**
//
// **AND THE CLIENT FORGED IT TWICE MORE ON TOP, SO THREE INDEPENDENT PATHS ARRIVED AT THE SAME
// SENTENCE:** `r.ok ? r.json() : { profile: null }` (a 500 becomes "no room"), `.catch(() => {})`
// (a dropped connection becomes "no room"), and a `.finally` that set the it-is-known flag on ALL
// of them. R18's distinction, on the one read that decides whether this member has a house.
//
// **THE GUARD IS PRESENCE, NOT `ok === true`, AND THAT IS MEASURED RATHER THAN PREFERRED (R53).**
// Eight fixtures in this repo mock this route as `{ profile: … }`, and a mock is a claim about a
// route's shape; requiring `ok: true` would have turned every rendered walk in the FAST tier red by
// raising the onboarding modal over it. So an answer is a 200 that is not `ok: false` AND that
// carries the `profile` KEY — and the server's refusal carries no `profile` key at all, so **both
// halves of the guard are load-bearing** and each is probed with an input only it can refuse (R28).
//
// **`sign-in` IS ITS OWN ARM AND IT IS ARGUED (R58's ruling), NOT COPIED FROM R47.** This route
// reads the signed session and answers on it, so 401 is an answer it CAN give — and it used to give
// that answer as `{ profile: null }` too. A member whose session lapsed between the session read and
// this one must not meet the create-a-room form: her fix is one tap, and it is not a second room.
//
// **A READ THAT DID NOT LAND SAYS NOTHING, DELIBERATELY.** R64's finding was a WRONG SENTENCE
// printed over a room that never arrived; the only thing this read decides is whether to raise a
// modal, so the honest answer to "we could not tell" is to leave the ordinary house standing and
// raise nothing. There is no sentence to get wrong here, and inventing one would be a second answer
// key for a question the screen never asks.
//
// `doFetch` is a PARAMETER (R46), so every answer the route can give is reachable with no browser,
// no session and no database.
export type ProfileAnswer<T> = {
  // **THE LOAD-BEARING FIELD, AND IT IS NOT `ok`.** `known` is "we learned whether she has a room".
  // Only a landed read knows, and the not-dismissible modal hangs off exactly this.
  known: boolean;
  kind: "ok" | "sign-in" | "refused" | "offline";
  // Meaningful ONLY when `known`. On every other arm it is null because nothing was learned — never
  // because she has nothing.
  profile: T | null;
  message: string;
};

export const profilePath = "/api/profile";

const nothingLearned = <T,>(kind: "sign-in" | "refused" | "offline", message = ""): ProfileAnswer<T> => ({
  known: false, kind, profile: null, message,
});

export async function readProfile<T>(
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<ProfileAnswer<T>> {
  let response: Response;
  try {
    response = await doFetch(profilePath);
  } catch {
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the server's word about her house.
    return nothingLearned<T>("offline");
  }
  const data = await response.json().catch(() => null) as
    | { ok?: boolean; profile?: unknown; message?: string }
    | null;
  const message = typeof data?.message === "string" ? data.message : "";
  if (response.status === 401) return nothingLearned<T>("sign-in", message);
  // PRESENCE, never truthiness (R53): `profile: null` is a real answer — a member with no room — and
  // `profile` ABSENT is a refusal. `data.ok === false` is the other half, and a refusal composed
  // before the read failed can carry both, which is what tells the two halves apart (R28).
  if (!response.ok || !data || data.ok === false || !("profile" in data)) {
    return nothingLearned<T>("refused", message);
  }
  return { known: true, kind: "ok", profile: (data.profile as T | null) ?? null, message: "" };
}

// **THE MODAL'S WHOLE CONDITION, AS A LAW A GATE RUNS** rather than a boolean assembled at the one
// call site that raises it (R64's four-way decision, made runnable). A room exists when the read
// LANDED and what it handed back carries a birth date — which is the same test App.tsx has always
// used to decide she has a house, said once, where something can ask it.
export function roomIsMissing<T extends { birthDate?: string }>(answer: ProfileAnswer<T>) {
  return answer.known && !(answer.profile && answer.profile.birthDate);
}

// >>> **IT #326 — "MY ENTIRE PAGE, SHELVES, CLOSET, EVERYTHING IS GONE."** <<< Nothing was lost:
// her profile read FAILED for about eleven minutes on 2026-09-26, and My Room answered a read that
// said nothing in the SAME words it says to a member who has no room ("Create your room..."). R71
// kept the modal down on that read; the room's own sentences were still saying "you have nothing".
// So an offline or refused read is its own state with its own sentence and a Try again. `sign-in`
// is NOT this state (her fix is one tap, and the session path already speaks to it), and a landed
// read, with or without a room, is never unreachable.
export function roomUnreachable<T>(answer: ProfileAnswer<T>) {
  return !answer.known && (answer.kind === "offline" || answer.kind === "refused");
}
